import React, { useState } from 'react';
import { X, Edit, Copy, Archive, ExternalLink, Eye } from 'lucide-react';
import { Chart } from '../components/Chart';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/Tabs';
interface SmartlinkDetailProps {
  smartlink: any;
  onClose: () => void;
  onEdit: () => void;
  onClone: () => void;
  onArchive: () => void;
}
export const SmartlinkDetail: React.FC<SmartlinkDetailProps> = ({
  smartlink,
  onClose,
  onEdit,
  onClone,
  onArchive
}) => {
  const [activeTab, setActiveTab] = useState('overview');
  const [period, setPeriod] = useState('7d');
  // Prepare chart data for segments
  const segmentPerformanceData = Object.entries(smartlink.performance.segmentPerformance).map(([key, value]: [string, any]) => ({
    name: key.charAt(0).toUpperCase() + key.slice(1).replace('_', ' '),
    cr: value.cr * 100,
    epc: value.epc,
    rpm: value.rpm
  }));
  // Prepare chart data for geos
  const geoPerformanceData = Object.entries(smartlink.performance.geoPerformance).map(([key, value]: [string, any]) => ({
    name: key.toUpperCase(),
    cr: value.cr * 100,
    epc: value.epc,
    rpm: value.rpm
  }));
  // Prepare chart data for offer contribution
  const offerContributionData = smartlink.performance.offerContribution.map((offer: any) => ({
    name: offer.offerName,
    revenue: offer.revenue,
    clicks: offer.clicks,
    conversions: offer.conversions,
    percentage: offer.percentage
  }));
  // Prepare chart data for RPM trend
  const rpmTrendData = smartlink.performance.rpmTrend.dates.map((date: string, index: number) => ({
    date,
    rpm: smartlink.performance.rpmTrend.rpm[index]
  }));
  return <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden" data-id="element-11953">
      <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/50 flex justify-between items-center" data-id="element-11954">
        <h2 className="text-xl font-bold text-gray-900 dark:text-white" data-id="element-11955">
          {smartlink.name}
        </h2>
        <button onClick={onClose} className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300" data-id="element-11956">
          <X size={20} data-id="element-11957" />
        </button>
      </div>
      <div className="p-6" data-id="element-11958">
        <div className="flex justify-between items-center mb-6" data-id="element-11959">
          <div className="flex space-x-3" data-id="element-11960">
            <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${smartlink.status === 'active' ? 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400' : 'bg-gray-100 text-gray-800 dark:bg-gray-900/20 dark:text-gray-400'}`} data-id="element-11961">
              {smartlink.status === 'active' ? 'Active' : 'Inactive'}
            </span>
            <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${smartlink.strategyId === 'rpm' ? 'bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-400' : smartlink.strategyId === 'epc' ? 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400' : smartlink.strategyId === 'cvr' ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-400' : 'bg-gray-100 text-gray-800 dark:bg-gray-900/20 dark:text-gray-400'}`} data-id="element-11962">
              {smartlink.strategyId === 'rpm' ? 'RPM Optimized' : smartlink.strategyId === 'epc' ? 'EPC Optimized' : smartlink.strategyId === 'cvr' ? 'CVR Optimized' : 'Manual (Priority/Weight)'}
            </span>
          </div>
          <div className="flex space-x-2" data-id="element-11963">
            <select className="px-3 py-2 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-700 rounded-md text-sm" value={period} onChange={e => setPeriod(e.target.value)} data-id="element-11964">
              <option value="today" data-id="element-11965">Today</option>
              <option value="yesterday" data-id="element-11966">Yesterday</option>
              <option value="7d" data-id="element-11967">Last 7 days</option>
              <option value="30d" data-id="element-11968">Last 30 days</option>
              <option value="90d" data-id="element-11969">Last 90 days</option>
            </select>
            <button onClick={onEdit} className="px-3 py-2 bg-blue-600 text-white rounded-md text-sm hover:bg-blue-700 transition-colors flex items-center" data-id="element-11970">
              <Edit size={16} className="mr-2" data-id="element-11971" />
              Edit
            </button>
            <button onClick={onClone} className="px-3 py-2 bg-green-600 text-white rounded-md text-sm hover:bg-green-700 transition-colors flex items-center" data-id="element-11972">
              <Copy size={16} className="mr-2" data-id="element-11973" />
              Clone
            </button>
            <button onClick={onArchive} className="px-3 py-2 bg-red-600 text-white rounded-md text-sm hover:bg-red-700 transition-colors flex items-center" data-id="element-11974">
              <Archive size={16} className="mr-2" data-id="element-11975" />
              Archive
            </button>
          </div>
        </div>
        <div className="mb-6" data-id="element-11976">
          <Tabs defaultValue="overview" data-id="element-11977">
            <TabsList data-id="element-11978">
              <TabsTrigger value="overview" data-id="element-11979">Overview</TabsTrigger>
              <TabsTrigger value="offers" data-id="element-11980">Offers</TabsTrigger>
              <TabsTrigger value="performance" data-id="element-11981">Performance</TabsTrigger>
              <TabsTrigger value="targeting" data-id="element-11982">Targeting</TabsTrigger>
              <TabsTrigger value="settings" data-id="element-11983">Settings</TabsTrigger>
            </TabsList>
            <TabsContent value="overview" className="pt-6" data-id="element-11984">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-11985">
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-5" data-id="element-11986">
                  <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4" data-id="element-11987">
                    Smartlink Details
                  </h3>
                  <div className="space-y-4" data-id="element-11988">
                    <div data-id="element-11989">
                      <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400" data-id="element-11990">
                        Description
                      </h4>
                      <p className="mt-1 text-sm text-gray-900 dark:text-white" data-id="element-11991">
                        {smartlink.description}
                      </p>
                    </div>
                    <div data-id="element-11992">
                      <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400" data-id="element-11993">
                        Strategy
                      </h4>
                      <p className="mt-1 text-sm text-gray-900 dark:text-white" data-id="element-11994">
                        {smartlink.strategyId === 'rpm' ? 'RPM Optimized' : smartlink.strategyId === 'epc' ? 'EPC Optimized' : smartlink.strategyId === 'cvr' ? 'CVR Optimized' : 'Manual (Priority/Weight)'}
                      </p>
                    </div>
                    <div data-id="element-11995">
                      <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400" data-id="element-11996">
                        Base URL
                      </h4>
                      <p className="mt-1 text-sm text-gray-900 dark:text-white break-all" data-id="element-11997">
                        {smartlink.baseUrl}
                      </p>
                    </div>
                    <div data-id="element-11998">
                      <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400" data-id="element-11999">
                        Offers in Pool
                      </h4>
                      <p className="mt-1 text-sm text-gray-900 dark:text-white" data-id="element-12000">
                        {smartlink.offersInPool.length} offers
                      </p>
                    </div>
                  </div>
                </div>
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-5" data-id="element-12001">
                  <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4" data-id="element-12002">
                    Performance Summary
                  </h3>
                  <div className="grid grid-cols-2 gap-4" data-id="element-12003">
                    <div data-id="element-12004">
                      <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400" data-id="element-12005">
                        Clicks
                      </h4>
                      <p className="mt-1 text-xl font-semibold text-gray-900 dark:text-white" data-id="element-12006">
                        {smartlink.performance.clicks.toLocaleString()}
                      </p>
                    </div>
                    <div data-id="element-12007">
                      <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400" data-id="element-12008">
                        Conversions
                      </h4>
                      <p className="mt-1 text-xl font-semibold text-gray-900 dark:text-white" data-id="element-12009">
                        {smartlink.performance.conversions.toLocaleString()}
                      </p>
                    </div>
                    <div data-id="element-12010">
                      <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400" data-id="element-12011">
                        Revenue
                      </h4>
                      <p className="mt-1 text-xl font-semibold text-gray-900 dark:text-white" data-id="element-12012">
                        ${smartlink.performance.revenue.toLocaleString()}
                      </p>
                    </div>
                    <div data-id="element-12013">
                      <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400" data-id="element-12014">
                        EPC
                      </h4>
                      <p className="mt-1 text-xl font-semibold text-gray-900 dark:text-white" data-id="element-12015">
                        ${smartlink.performance.epc.toFixed(2)}
                      </p>
                    </div>
                    <div data-id="element-12016">
                      <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400" data-id="element-12017">
                        Conversion Rate
                      </h4>
                      <p className="mt-1 text-xl font-semibold text-gray-900 dark:text-white" data-id="element-12018">
                        {(smartlink.performance.cr * 100).toFixed(2)}%
                      </p>
                    </div>
                    <div data-id="element-12019">
                      <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400" data-id="element-12020">
                        RPM
                      </h4>
                      <p className="mt-1 text-xl font-semibold text-gray-900 dark:text-white" data-id="element-12021">
                        ${smartlink.performance.rpm.toFixed(2)}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
              <div className="grid grid-cols-1 mt-6" data-id="element-12022">
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-5" data-id="element-12023">
                  <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4" data-id="element-12024">
                    RPM Trend
                  </h3>
                  <div className="h-80" data-id="element-12025">
                    <Chart title="" data={rpmTrendData} type="line" height={300} yAxisLabel="RPM ($)" xAxisDataKey="date" colors={['#3b82f6']} dataKeys={['rpm']} data-id="element-12026" />
                  </div>
                </div>
              </div>
            </TabsContent>
            <TabsContent value="offers" className="pt-6" data-id="element-12027">
              <div className="grid grid-cols-1 gap-6" data-id="element-12028">
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-5" data-id="element-12029">
                  <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4" data-id="element-12030">
                    Offers in Pool
                  </h3>
                  <div className="overflow-x-auto" data-id="element-12031">
                    <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700" data-id="element-12032">
                      <thead className="bg-gray-50 dark:bg-gray-800" data-id="element-12033">
                        <tr data-id="element-12034">
                          <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider" data-id="element-12035">
                            Offer
                          </th>
                          <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider" data-id="element-12036">
                            Weight
                          </th>
                          <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider" data-id="element-12037">
                            Priority
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700" data-id="element-12038">
                        {smartlink.offersInPool.map((offer: any) => <tr key={offer.offerId} data-id="element-12039">
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white" data-id="element-12040">
                              {offer.offerName}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white" data-id="element-12041">
                              {offer.weight}%
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white" data-id="element-12042">
                              {offer.priority}
                            </td>
                          </tr>)}
                      </tbody>
                    </table>
                  </div>
                </div>
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-5" data-id="element-12043">
                  <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4" data-id="element-12044">
                    Offer Contribution
                  </h3>
                  <div className="h-80" data-id="element-12045">
                    <Chart title="" data={offerContributionData} type="bar" height={300} yAxisLabel="Revenue ($)" colors={['#3b82f6']} dataKeys={['revenue']} data-id="element-12046" />
                  </div>
                  <div className="mt-6 overflow-x-auto" data-id="element-12047">
                    <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700" data-id="element-12048">
                      <thead className="bg-gray-50 dark:bg-gray-800" data-id="element-12049">
                        <tr data-id="element-12050">
                          <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider" data-id="element-12051">
                            Offer
                          </th>
                          <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider" data-id="element-12052">
                            Clicks
                          </th>
                          <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider" data-id="element-12053">
                            Conversions
                          </th>
                          <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider" data-id="element-12054">
                            Revenue
                          </th>
                          <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider" data-id="element-12055">
                            EPC
                          </th>
                          <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider" data-id="element-12056">
                            CR
                          </th>
                          <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider" data-id="element-12057">
                            RPM
                          </th>
                          <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider" data-id="element-12058">
                            %
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700" data-id="element-12059">
                        {smartlink.performance.offerContribution.map((offer: any) => <tr key={offer.offerId} data-id="element-12060">
                              <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white" data-id="element-12061">
                                {offer.offerName}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white" data-id="element-12062">
                                {offer.clicks.toLocaleString()}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white" data-id="element-12063">
                                {offer.conversions.toLocaleString()}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white" data-id="element-12064">
                                ${offer.revenue.toLocaleString()}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white" data-id="element-12065">
                                ${offer.epc.toFixed(2)}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white" data-id="element-12066">
                                {(offer.cr * 100).toFixed(2)}%
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white" data-id="element-12067">
                                ${offer.rpm.toFixed(2)}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white" data-id="element-12068">
                                {offer.percentage}%
                              </td>
                            </tr>)}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </TabsContent>
            <TabsContent value="performance" className="pt-6" data-id="element-12069">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-12070">
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-5" data-id="element-12071">
                  <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4" data-id="element-12072">
                    Segment Performance
                  </h3>
                  <div className="h-80" data-id="element-12073">
                    <Chart title="" data={segmentPerformanceData} type="bar" height={300} yAxisLabel="Value" colors={['#10b981', '#3b82f6', '#8b5cf6']} dataKeys={['cr', 'epc', 'rpm']} data-id="element-12074" />
                  </div>
                </div>
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-5" data-id="element-12075">
                  <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4" data-id="element-12076">
                    GEO Performance
                  </h3>
                  <div className="h-80" data-id="element-12077">
                    <Chart title="" data={geoPerformanceData} type="bar" height={300} yAxisLabel="Value" colors={['#10b981', '#3b82f6', '#8b5cf6']} dataKeys={['cr', 'epc', 'rpm']} data-id="element-12078" />
                  </div>
                </div>
              </div>
              <div className="grid grid-cols-1 mt-6" data-id="element-12079">
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-5" data-id="element-12080">
                  <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4" data-id="element-12081">
                    Performance Details
                  </h3>
                  <div className="overflow-x-auto" data-id="element-12082">
                    <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700" data-id="element-12083">
                      <thead className="bg-gray-50 dark:bg-gray-800" data-id="element-12084">
                        <tr data-id="element-12085">
                          <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider" data-id="element-12086">
                            Segment
                          </th>
                          <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider" data-id="element-12087">
                            Conversion Rate
                          </th>
                          <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider" data-id="element-12088">
                            EPC
                          </th>
                          <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider" data-id="element-12089">
                            RPM
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700" data-id="element-12090">
                        {Object.entries(smartlink.performance.segmentPerformance).map(([key, value]: [string, any]) => <tr key={key} data-id="element-12091">
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white" data-id="element-12092">
                              {key.charAt(0).toUpperCase() + key.slice(1).replace('_', ' ')}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white" data-id="element-12093">
                              {(value.cr * 100).toFixed(2)}%
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white" data-id="element-12094">
                              ${value.epc.toFixed(2)}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white" data-id="element-12095">
                              ${value.rpm.toFixed(2)}
                            </td>
                          </tr>)}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </TabsContent>
            <TabsContent value="targeting" className="pt-6" data-id="element-12096">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-12097">
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-5" data-id="element-12098">
                  <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4" data-id="element-12099">
                    Target GEOs
                  </h3>
                  <div className="space-y-4" data-id="element-12100">
                    <div data-id="element-12101">
                      <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400" data-id="element-12102">
                        Included GEOs
                      </h4>
                      <div className="mt-1 flex flex-wrap gap-1" data-id="element-12103">
                        {smartlink.geos.map((geo: string) => <span key={geo} className="px-2 py-1 text-xs bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-400 rounded" data-id="element-12104">
                            {geo.toUpperCase()}
                          </span>)}
                      </div>
                    </div>
                  </div>
                </div>
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-5" data-id="element-12105">
                  <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4" data-id="element-12106">
                    Segment Targeting
                  </h3>
                  <div className="space-y-4" data-id="element-12107">
                    <div data-id="element-12108">
                      <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400" data-id="element-12109">
                        Target Segments
                      </h4>
                      <div className="mt-1 flex flex-wrap gap-1" data-id="element-12110">
                        {smartlink.segments.map((segment: string) => <span key={segment} className="px-2 py-1 text-xs bg-indigo-100 text-indigo-800 dark:bg-indigo-900/20 dark:text-indigo-400 rounded" data-id="element-12111">
                            {segment.split('_').map((word: string) => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')}
                          </span>)}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </TabsContent>
            <TabsContent value="settings" className="pt-6" data-id="element-12112">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-12113">
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-5" data-id="element-12114">
                  <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4" data-id="element-12115">
                    URL Parameters
                  </h3>
                  <div className="space-y-4" data-id="element-12116">
                    <div data-id="element-12117">
                      <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400" data-id="element-12118">
                        Base URL
                      </h4>
                      <p className="mt-1 text-sm text-gray-900 dark:text-white break-all" data-id="element-12119">
                        {smartlink.baseUrl}
                      </p>
                    </div>
                    <div data-id="element-12120">
                      <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400" data-id="element-12121">
                        URL Parameters
                      </h4>
                      <div className="mt-1 space-y-2" data-id="element-12122">
                        {smartlink.urlParams && Object.entries(smartlink.urlParams).map(([key, value]: [string, any]) => <div key={key} className="flex justify-between" data-id="element-12123">
                                <span className="text-sm text-gray-900 dark:text-white" data-id="element-12124">
                                  {key}
                                </span>
                                <span className="text-sm text-gray-500 dark:text-gray-400" data-id="element-12125">
                                  {value}
                                </span>
                              </div>)}
                      </div>
                    </div>
                    <div data-id="element-12126">
                      <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400" data-id="element-12127">
                        Full Tracking URL
                      </h4>
                      <div className="mt-1 p-3 bg-gray-50 dark:bg-gray-900 rounded-md" data-id="element-12128">
                        <code className="text-xs text-gray-900 dark:text-gray-300 break-all" data-id="element-12129">
                          {smartlink.baseUrl}?
                          {Object.entries(smartlink.urlParams || {}).map(([key, value]) => `${key}=${value}`).join('&')}
                        </code>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-5" data-id="element-12130">
                  <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4" data-id="element-12131">
                    Caps & Failover
                  </h3>
                  <div className="space-y-4" data-id="element-12132">
                    <div data-id="element-12133">
                      <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400" data-id="element-12134">
                        Daily Cap
                      </h4>
                      <p className="mt-1 text-sm text-gray-900 dark:text-white" data-id="element-12135">
                        {smartlink.caps.daily.toLocaleString()}
                      </p>
                    </div>
                    <div data-id="element-12136">
                      <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400" data-id="element-12137">
                        Hourly Cap
                      </h4>
                      <p className="mt-1 text-sm text-gray-900 dark:text-white" data-id="element-12138">
                        {smartlink.caps.hourly.toLocaleString()}
                      </p>
                    </div>
                    <div data-id="element-12139">
                      <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400" data-id="element-12140">
                        Spillover Rules
                      </h4>
                      <p className="mt-1 text-sm text-gray-900 dark:text-white" data-id="element-12141">
                        {smartlink.caps.spilloverRules === 'next_priority' ? 'Use next offer by priority' : smartlink.caps.spilloverRules}
                      </p>
                    </div>
                    <div data-id="element-12142">
                      <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400" data-id="element-12143">
                        Failover Enabled
                      </h4>
                      <p className="mt-1 text-sm text-gray-900 dark:text-white" data-id="element-12144">
                        {smartlink.failover.enabled ? 'Yes' : 'No'}
                      </p>
                    </div>
                    <div data-id="element-12145">
                      <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400" data-id="element-12146">
                        Next Eligible by Priority
                      </h4>
                      <p className="mt-1 text-sm text-gray-900 dark:text-white" data-id="element-12147">
                        {smartlink.failover.nextEligibleByPriority ? 'Yes' : 'No'}
                      </p>
                    </div>
                    <div data-id="element-12148">
                      <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400" data-id="element-12149">
                        404 Protection
                      </h4>
                      <p className="mt-1 text-sm text-gray-900 dark:text-white" data-id="element-12150">
                        {smartlink.failover.protection404 ? 'Yes' : 'No'}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </TabsContent>
          </Tabs>
        </div>
      </div>
    </div>;
};